import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import usePaymentTransactions from '../hooks/usePaymentTransactions';
import { RefreshCw } from 'lucide-react';
import { formatINR } from "../utils/formatINR";

// created once; formatting per row is slow on large tables
const dateFormatter = new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
const timeFormatter = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

const TYPE_LABELS = {
  wallet_topup: 'Wallet Topup',
  charging: 'Charging',
  transfer: 'Transfer',
  qr_topup: 'QR Topup',
  refund: 'Refund',
  vehicle_to_wallet: 'Vehicle to Wallet',
  station_to_wallet: 'Station to Wallet',
  cashback: 'Cashback',
};

// each type keeps its own text color (darker shades so they stay readable on grey rows)
const TYPE_STYLES = {
  wallet_topup: 'text-brand-600',
  charging: 'text-green-700',
  transfer: 'text-violet-600',
  qr_topup: 'text-pink-600',
  refund: 'text-cyan-700',
  vehicle_to_wallet: 'text-teal-700',
  station_to_wallet: 'text-neutral-600',
  cashback: 'text-amber-700',
};

const typeStyle = (tx) =>
  TYPE_STYLES[tx.transaction_type] ||
  (tx.payment_method === 'qr_code' ? TYPE_STYLES.qr_topup : 'text-neutral-800');

const STATUS_STYLES = {
  completed: 'bg-green-50 text-green-700 ring-green-600/20',
  pending: 'bg-amber-50 text-amber-700 ring-amber-600/25',
  captured: 'bg-brand-50 text-brand-700 ring-brand-600/20',
  failed: 'bg-red-50 text-red-700 ring-red-600/20',
};

const PAGE_SIZE = 100;

const updatedTimeFormatter = new Intl.DateTimeFormat('en-IN', { hour: '2-digit', minute: '2-digit' });
const updatedDateTimeFormatter = new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

// "today at 09:42 pm", or "30 Oct, 09:42 pm" for older data
const formatUpdatedAt = (date) =>
  date.toDateString() === new Date().toDateString()
    ? `today at ${updatedTimeFormatter.format(date)}`
    : updatedDateTimeFormatter.format(date);

export default function PaymentTransactionsPage() {
  const [dateRange, setDateRange] = useState({ fromDate: '', toDate: '' });

  // Pass dateRange to hook
  const { transactions, isLoading, error, message, lastUpdated, refetch } = usePaymentTransactions(dateRange);

  const [transactionFilter, setTransactionFilter] = useState(""); // "" means no filter


  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Show server message as toast
  useEffect(() => {
    if (message) toast.success(message);
  }, [message]);

  // Normalize server data (memoized so it's computed during render, not in an extra effect pass)
  const localTransactions = useMemo(() => {
    if (!transactions) return [];
    return transactions.map(tx => ({
        amount: tx.amount || tx.Amount || 0,
        final_cost: tx.final_cost || tx.FinalCost || 0,
        status: tx.status || tx.Status || '',
        date: tx.created_at || tx.CreatedAt
          ? new Date(tx.created_at || tx.CreatedAt)
          : null,
        station_id: tx.station_id?.Valid ? tx.station_id.String : tx.StationID || '',
        gun_id: tx.gun_id?.Valid ? tx.gun_id.String : tx.GunID || '',
        user_id: tx.user_id?.Valid ? tx.user_id.String : tx.UserID || '',
        vehicle_id: tx.ccs2_vehicle_id?.Valid ? tx.ccs2_vehicle_id.String : tx.CCS2VehicleID || '',
        description: tx.description?.Valid ? tx.description.String : tx.Description || '',
        payment_reference_id: tx.payment_reference_id?.Valid ? tx.payment_reference_id.String : tx.PaymentReferenceID || '',
        transaction_type: tx.transaction_type || tx.TransactionType || '',
        payment_method_type: tx.payment_method_type || tx.PaymentMethodType || '',
        actions: tx.actions || []
      }));
  }, [transactions]);

  const filteredTransactions = useMemo(
    () => localTransactions.filter(tx => !transactionFilter || tx.transaction_type === transactionFilter),
    [localTransactions, transactionFilter]
  );

  // start from the first page again whenever the data or filter changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filteredTransactions]);

  const hasDateFilter = dateRange.fromDate || dateRange.toDate;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="page-title">Payment Transactions</h1>
          <p className="page-subtitle">
            {hasDateFilter ? 'Transactions in the selected date range' : 'Transactions from the last 7 days'}
          </p>
        </div>
        <div className="flex flex-col items-start gap-1 sm:items-end">
          <p className="text-13 text-muted">
            <span className="font-semibold text-fg tabular-nums">{filteredTransactions.length}</span> transactions
          </p>
          <div className="flex items-center gap-2 text-xs text-muted">
            <span>
              {isLoading ? 'Updating…' : lastUpdated ? `Last updated ${formatUpdatedAt(lastUpdated)}` : ''}
            </span>
            {!isLoading && (
              <button
                type="button"
                onClick={refetch}
                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium text-brand-600 hover:bg-brand-50 hover:text-brand-700"
              >
                <RefreshCw className="h-3 w-3" />
                Refresh
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 flex flex-col gap-3 lg:flex-row lg:items-end">
        <div className="flex flex-col gap-1.5">
          <label className="field-label">Date range</label>
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="date"
              value={dateRange.fromDate}
              onChange={e => setDateRange(prev => ({ ...prev, fromDate: e.target.value }))}
              className="input w-[150px]"
            />
            <span className="text-13 text-muted">to</span>
            <input
              type="date"
              value={dateRange.toDate}
              onChange={e => setDateRange(prev => ({ ...prev, toDate: e.target.value }))}
              className="input w-[150px]"
            />
            {hasDateFilter && (
              <button
                onClick={() => setDateRange({ fromDate: '', toDate: '' })}
                className="btn-ghost"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1.5 lg:ml-auto">
          <label className="field-label">Type</label>
          <select
            value={transactionFilter}
            onChange={(e) => setTransactionFilter(e.target.value)}
            className="input pr-8 min-w-[200px]"
          >
            <option value="">All Types</option>
            {Object.entries(TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-13">
            <thead className="border-b border-line">
              <tr>
                <th className="h-10 px-4 text-right uppercase">Amount</th>
                <th className="h-10 px-4 text-right uppercase">Final Cost</th>
                <th className="h-10 px-4 text-left uppercase">Status</th>
                <th className="h-10 px-4 text-left uppercase">Type</th>
                <th className="h-10 px-4 text-left uppercase">Station ID</th>
                <th className="h-10 px-4 text-left uppercase">Gun ID</th>
                <th className="h-10 px-4 text-left uppercase">User ID</th>
                <th className="h-10 px-4 text-left uppercase">Vehicle ID</th>
                <th className="h-10 px-4 text-left uppercase">Date</th>
                <th className="h-10 px-4 text-left uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/70">
              {filteredTransactions
                .slice(0, visibleCount)
                .map((tx, index) => (
                <tr key={index} className="odd:bg-surface even:bg-neutral-100 hover:!bg-brand-50/60">
                  <td className="px-4 py-3 text-right text-13 font-semibold text-fg whitespace-nowrap tabular-nums">{formatINR(tx.amount)}</td>
                  <td className="px-4 py-3 text-right text-13 font-medium text-neutral-700 whitespace-nowrap tabular-nums">{formatINR(tx.final_cost)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`badge capitalize ${STATUS_STYLES[tx.status] || STATUS_STYLES.failed}`}>
                      <span className="badge-dot opacity-80" />
                      {tx.status || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`font-medium ${typeStyle(tx)}`}>
                      {TYPE_LABELS[tx.transaction_type] || tx.transaction_type || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-neutral-600 whitespace-nowrap">{tx.station_id || <span className="text-neutral-300">—</span>}</td>
                  <td className="px-4 py-3 text-neutral-600 whitespace-nowrap">{tx.gun_id || <span className="text-neutral-300">—</span>}</td>
                  <td className="px-4 py-3 text-neutral-600 whitespace-nowrap">{tx.user_id || <span className="text-neutral-300">—</span>}</td>
                  <td className="px-4 py-3 text-neutral-600 whitespace-nowrap">{tx.vehicle_id || <span className="text-neutral-300">—</span>}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {tx.date && !isNaN(tx.date) ? (
                      <>
                        <div className="text-neutral-800">{dateFormatter.format(tx.date)}</div>
                        <div className="text-xs text-muted">{timeFormatter.format(tx.date)}</div>
                      </>
                    ) : (
                      <span className="text-neutral-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 space-x-3 whitespace-nowrap">
                    {tx.actions?.map((action, idx) => (
                      <button key={idx} className={`text-xs font-semibold ${
                        action === 'Retry' ? 'text-red-600 hover:text-red-800' :
                        action === 'View' ? 'text-brand-600 hover:text-brand-700' :
                        'text-neutral-600 hover:text-ink'
                      }`}>
                        {action}
                      </button>
                    ))}
                  </td>
                </tr>
              ))}

              {filteredTransactions.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-13 text-muted">
                    No transactions found
                  </td>
                </tr>
              )}

              {isLoading && localTransactions.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-13 text-muted">
                    <span className="inline-block w-4 h-4 mr-2 align-[-2px] rounded-full border-2 border-neutral-300 border-t-brand-600 animate-spin" />
                    Loading transactions...
                  </td>
                </tr>
              )}

              {error && (
                <tr>
                  <td colSpan={10} className="px-4 py-3 text-center text-13 text-red-700 bg-red-50">
                    {error}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {filteredTransactions.length > visibleCount && (
          <div className="px-4 py-3 border-t border-line flex items-center justify-between text-13">
            <span className="text-muted">
              Showing <span className="font-medium text-fg tabular-nums">{visibleCount}</span> of{' '}
              <span className="font-medium text-fg tabular-nums">{filteredTransactions.length}</span>
            </span>
            <button
              onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
              className="btn-secondary"
            >
              Show {Math.min(PAGE_SIZE, filteredTransactions.length - visibleCount)} more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
